import { useNavigate, useParams } from 'react-router-dom';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  CAPTURE_WORKFLOWS,
  readStoredWorkflow,
  storeWorkflow,
} from '@/lib/capture-workflows';
import type { CaptureWorkflow } from '@/lib/api/types';

/**
 * Names the job before the shutter.
 *
 * The same nameplate is read differently for a purchase order than for a
 * stock count, and the choice made here reaches the extraction model as its
 * task instruction (`apps/capture-api/src/extraction/capture-workflows.ts`).
 * The proof of concept opened on this step, which is where the rule came
 * from; a capture with no job named still works and extracts against the
 * item standard alone.
 *
 * The choice is remembered, because an operator works a run of one job
 * rather than switching per item. The viewfinder shows it back with a way
 * to change it, so a remembered choice is never a silent one.
 */
export function CaptureWorkflowPicker() {
  const navigate = useNavigate();
  const { jobId = '' } = useParams();
  const current = readStoredWorkflow();

  function choose(workflow: CaptureWorkflow) {
    storeWorkflow(workflow);
    navigate(`/jobs/${encodeURIComponent(jobId)}/capture`, { replace: true });
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-lg font-medium">What are you capturing for?</h1>
        <p className="mt-1 text-sm text-muted">
          This tells the reader what to look for on the item.
        </p>
      </div>

      <ul className="flex flex-col gap-2 sm:grid sm:grid-cols-2 lg:grid-cols-3">
        {CAPTURE_WORKFLOWS.map((option) => (
          <li key={option.id}>
            <button
              type="button"
              onClick={() => choose(option.id)}
              aria-current={option.id === current ? 'true' : undefined}
              className={cn(
                'flex h-full min-h-16 w-full flex-col items-start justify-center gap-0.5 rounded-card border border-border px-4 py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                option.id === current && 'border-brand bg-brand/10',
              )}
            >
              <span className="flex items-center gap-2 text-sm font-medium">
                {option.title}
                {option.id === current && <Check className="size-4 text-brand" aria-hidden="true" />}
              </span>
              <span className="text-xs text-muted">{option.tagline}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
